import {
    AdditionalBidParams,
    Channels,
    IChannel,
    IPopoverRendererParams,
} from "@/ts/common-types";
import {
    bidUpdateApplyEventHandler,
    getGridType,
    internationalizeNumericValue,
    isDisabledUI,
    parseInternationalizedValue,
} from "@/ts/utils";
import { Button, Popover, Switch, Tag } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import $ from "jquery";
import BidInputComponent from "../misc/BidInputComponent";
import { isEqual, isNull } from "underscore";
import { showToast } from "@/ts/modals";
import { setUpdateGridData } from "@/ts/filters";
import * as Sentry from "@sentry/react";
import { useGetModulesData } from "@/hooks/AllCustomHooks";

const PopOverBidCellRenderer = (params: IPopoverRendererParams) => {
    const currencySymbol = useSelector(
        (state: IChannel) => state.channelCurrencySymbol.value
    );
    const identifier = params.data.sys_ams_keyword_id;
    const channel = useSelector((state: IChannel) => state.channel.value);

    const previousExactValue: number = params.data.bid;
    const previousSmartValue: number = params.data.smart_bid;
    const previousIsSmartValue: boolean = params.data.is_smart || false;
    const previousSlotsValue: Array<number> = params.data.keyword_slots || [];

    const [programType] = useState<string>(params.data.program_type);
    const otherAttributes = {
        baseProgramType: programType,
        costType: params.data.cost_type || undefined,
        campaignBudget: params.data.campaign_budget,
        minBidValue: params.data.min_bid_value,
        maxBidValue: params.data.max_bid_value,
        recordType: params.data.record_type,
    };
    const [exactBidLimitMessage, setExactBidLimitMessage] =
        useState<string>("");
    const [smartBidLimitMessage, setSmartBidLimitMessage] =
        useState<string>("");

    const [isUIDisabled] = useState<boolean>(
        isDisabledUI([params.data.serving_status, params.data.campaign_status])
    );
    const [exactBid, setExactBid] = useState<string | number>(
        internationalizeNumericValue(params.data[params.column.colId])
    );
    const [smartBid, setSmartBid] = useState<string | number>(
        internationalizeNumericValue(params.data.smart_bid)
    );
    const [keywordBidSlots, setKeywordBidSlots] = useState<Array<number>>(
        params.data.keyword_slots || []
    );

    const [isSmartMatch, setIsSmartMatch] = useState<boolean>(
        params.data.is_smart || false
    );
    const [isPopoverVisible, setIsPopoverVisible] = useState(false);
    const [isSubmitBtnDisabled, setIsSubmitBtnDisabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const channelOptions = useGetModulesData({ type: "channel-options" });
    const gridParams = useGetModulesData({
        type: "grid-params",
        subGridType: getGridType(),
    });

    const onPopoverVisibleChange = (visible: boolean) => {
        setIsPopoverVisible(!isUIDisabled && visible);

        if (!visible) {
            setExactBid(previousExactValue);
            setSmartBid(previousSmartValue);
            setIsSmartMatch(
                isNull(previousIsSmartValue) ? false : previousIsSmartValue
            );
            $(`#${identifier}-Smart-bid-limit-span`).hide();
            $(`#${identifier}-Exact-bid-limit-span`).hide();
            setExactBidLimitMessage("");
            setSmartBidLimitMessage("");
            params.data.is_smart
                ? $(`#${identifier}-Smart-winning-limit-span`).show()
                : $(`#${identifier}-Smart-winning-limit-span`).hide();
        }
    };

    const handleSmartCheckChange = (checked: boolean) => {
        setIsSmartMatch(checked);
        if (!checked) {
            $(`#${identifier}-Smart-bid-limit-span`).hide();
            $(`#${identifier}-Smart-winning-limit-span`).hide();
            setSmartBid(0);
            setIsSubmitBtnDisabled(false);
        } else {
            $(`#${identifier}-Smart-winning-limit-span`).show();
            if (!+smartBid) {
                setSmartBidLimitMessage(`Please enter a CPM value`);
                $(`#${identifier}-Smart-bid-limit-span`).show();
                setIsSubmitBtnDisabled(true);
            }
        }
    };

    const handleBidSubmit = async () => {
        const isSlotChangeRequired = gridParams["bid"].isSlotChangeRequired;
        const parsedExactBidValue = +parseInternationalizedValue(exactBid);
        const parsedSmartBidValue = +parseInternationalizedValue(smartBid);
        if (
            isSlotChangeRequired &&
            ((!parsedExactBidValue && keywordBidSlots.length) ||
                (!keywordBidSlots.length && parsedExactBidValue) ||
                (parsedExactBidValue === previousExactValue &&
                    isEqual(keywordBidSlots, previousSlotsValue)))
        ) {
            showToast(
                "danger",
                "Please update the bid value or slots appropriately."
            );
            return;
        } else if (
            parsedExactBidValue === previousExactValue &&
            parsedSmartBidValue === (previousSmartValue || 0) &&
            isSmartMatch === previousIsSmartValue
        ) {
            showToast("danger", "Please update the value.");
            return;
        }
        setLoading(true);
        try {
            const additionalParams: AdditionalBidParams = {
                ...(channel !== Channels.TALABAT && {
                    isSmartMatch: isNull(isSmartMatch) ? false : isSmartMatch,
                    smartBid: +parsedSmartBidValue,
                }),
                slots: keywordBidSlots.length ? keywordBidSlots : null,
            };
            const response = await bidUpdateApplyEventHandler(
                parsedExactBidValue,
                identifier.toString(),
                otherAttributes,
                additionalParams
            );

            if (!response.status) {
                throw new Error(response.message);
            }

            showToast("success", response.message);
            !params?.isModalComponent &&
                setUpdateGridData({
                    params,
                    value: parsedExactBidValue,
                    isReduxState: true,
                    additionalValuesToUpdate: {
                        smart_bid: parsedSmartBidValue,
                        is_smart: isSmartMatch,
                    },
                });
        } catch (err) {
            console.error(err);
            $(`#Exact-update-field-${identifier}`).val(previousExactValue);
            $(`#Smart-update-field-${identifier}`).val(previousSmartValue);
            setExactBid(previousExactValue);
            setSmartBid(previousSmartValue);
            const resErrMsg = (err as Error).message;
            const errorMessage = `Error While Updating Bid for ${params.data.keyword_name}`;
            showToast(
                "danger",
                `${errorMessage} ${resErrMsg && ": " + resErrMsg}`
            );
            Sentry.captureException(err);
        }
        setLoading(false);
    };

    const popoverContent = () => (
        <>
            <div
                className={`text-secondary`}
                style={{
                    width: "16rem",
                    fontSize: "13px",
                }}
            >
                <BidInputComponent
                    {...{
                        identifier,
                        inputValue: exactBid,
                        previousValue: previousExactValue,
                        isUIDisabled,
                        setBidValue: setExactBid,
                        bidType: gridParams["bid"].bidType,
                        bidLimitMessage: exactBidLimitMessage,
                        setBidLimitMessage: setExactBidLimitMessage,
                        params,
                        setIsSubmitBtnDisabled,
                        keywordBidSlots,
                        setKeywordBidSlots,
                        maxBidLimitRecordTypes:
                            gridParams["bid"].maxBidLimitRecordTypes,
                        canBidInputBeDisabled:
                            gridParams["bid"].canBidInputBeDisabled,
                        isSlotChangeRequired:
                            gridParams["bid"].isSlotChangeRequired,
                    }}
                />
                {!!channelOptions.isSmartInfoRequired &&
                    params.data.record_type === "keyword" && (
                        <>
                            <div className={`d-flex align-items-center my-3`}>
                                <label className="fw-bold">Smart Match</label>
                                <Switch
                                    size="small"
                                    className="mx-2"
                                    checked={isSmartMatch}
                                    onChange={handleSmartCheckChange}
                                />
                            </div>
                            <BidInputComponent
                                {...{
                                    identifier,
                                    inputValue: smartBid,
                                    previousValue: previousSmartValue,
                                    isUIDisabled,
                                    setBidValue: setSmartBid,
                                    bidType: "Smart",
                                    bidLimitMessage: smartBidLimitMessage,
                                    setBidLimitMessage: setSmartBidLimitMessage,
                                    params,
                                    isSmartMatch,
                                    setIsSubmitBtnDisabled,
                                    keywordBidSlots,
                                    setKeywordBidSlots,
                                    maxBidLimitRecordTypes:
                                        gridParams["bid"]
                                            .maxBidLimitRecordTypes,
                                    canBidInputBeDisabled:
                                        gridParams["bid"].canBidInputBeDisabled,
                                    isSlotChangeRequired:
                                        gridParams["bid"].isSlotChangeRequired,
                                }}
                            />
                        </>
                    )}
                <div className="d-flex flex-row-reverse mt-3">
                    <Button
                        size="small"
                        type="primary"
                        style={{
                            fontSize: "12px",
                        }}
                        disabled={isSubmitBtnDisabled}
                        onClick={handleBidSubmit}
                        loading={loading}
                    >
                        Submit
                    </Button>
                </div>
            </div>
        </>
    );

    useEffect(() => {
        if (!exactBidLimitMessage && !smartBidLimitMessage) {
            setIsSubmitBtnDisabled(false);
        }
    }, [exactBidLimitMessage, smartBidLimitMessage]);

    return (
        <>
            {params.node.rowPinned ? null : (
                <div
                    style={{
                        marginTop: "5px",
                    }}
                >
                    <Popover
                        styles={{
                            body: {
                                padding: "18px",
                                borderRadius: "0.5rem",
                            },
                        }}
                        placement="right"
                        trigger="click"
                        open={isPopoverVisible}
                        onOpenChange={onPopoverVisibleChange}
                        content={popoverContent}
                    >
                        <Tag
                            className="bid-tag"
                            style={{
                                cursor: isUIDisabled
                                    ? "not-allowed"
                                    : "pointer",
                            }}
                        >
                            {params.data[params.column.colId] ||
                            params.data[params.column.colId] === 0
                                ? ` ${currencySymbol}${internationalizeNumericValue(
                                      params.data[params.column.colId]
                                  )}`
                                : "Set Custom Bid"}
                        </Tag>
                        {params.data.is_smart && (
                            <Tag
                                style={{
                                    display: "block",
                                    fontSize: "10px",
                                    border: "none",
                                    textAlign: "left",
                                    marginTop: "2px",
                                }}
                            >
                                Smart:{" "}
                                {`${currencySymbol}${internationalizeNumericValue(
                                    params.data.smart_bid
                                )}`}
                            </Tag>
                        )}
                    </Popover>
                </div>
            )}
        </>
    );
};

export default PopOverBidCellRenderer;
